/**
 * Entry-page picker for a person who can work in two or more places: their master accounts and their
 * direct organizations, as two groups. A master account's client is reached only from the board.
 */
import { Button, Icon } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useSwitchAccount, SwitchTargetKind, type SwitchTarget } from '../hooks/use-switch-account';

interface AccountSelectionScreenProps {
  /** Called when the user confirms staying where the session already acts. */
  onContinue: () => void;
}

export function AccountSelectionScreen({ onContinue }: AccountSelectionScreenProps) {
  const t = useFeatureTranslation('auth');
  const { targets, activeTargetId, switchTo, switching } = useSwitchAccount();

  const masterAccounts = targets.filter(target => target.kind === SwitchTargetKind.MASTER_ACCOUNT);
  const organizations = targets.filter(target => target.kind === SwitchTargetKind.ACCOUNT);

  const handlePick = (targetId: string) => {
    if (targetId === activeTargetId) {
      onContinue();
      return;
    }
    void switchTo(targetId);
  };

  const renderGroup = (title: string, testId: string, entries: SwitchTarget[]) =>
    entries.length > 0 && (
      <section className='flex flex-col gap-sm' data-testid={testId}>
        <h2 className='text-xs font-medium uppercase tracking-wide text-foreground-muted'>
          {title}
        </h2>
        {entries.map(entry => {
          const isActive = entry.id === activeTargetId;
          return (
            <Button
              key={entry.id}
              variant={isActive ? 'primary' : 'secondary'}
              size='full'
              disabled={switching}
              onClick={() => handlePick(entry.id)}
              aria-current={isActive ? 'true' : undefined}
              testId={`account-selection-item-${entry.id}`}
            >
              <span className='flex flex-1 items-center gap-sm'>
                {isActive && <Icon name='check' />}
                {entry.name}
              </span>
            </Button>
          );
        })}
      </section>
    );

  return (
    <div className='flex items-center justify-center min-h-dvh p-lg'>
      <div className='flex flex-col gap-lg w-full max-w-md'>
        <div className='flex flex-col gap-xs text-center'>
          <h1 className='text-xl font-semibold text-foreground-base'>
            {t('accountSelection.title')}
          </h1>
          <p className='text-sm text-foreground-muted'>{t('accountSelection.description')}</p>
        </div>

        {renderGroup(
          t('accountSelection.masterAccountsGroup'),
          'account-selection-master-accounts',
          masterAccounts
        )}
        {renderGroup(
          t('accountSelection.organizationsGroup'),
          'account-selection-organizations',
          organizations
        )}
      </div>
    </div>
  );
}
